export const formatCurrency = (amount: string | number, decimals = 2, minAmount = 0.01) => {
  if (!Number.isNaN(amount)) {
    const num = Number(amount)
    const numAmount = Number.isNaN(num) ? 0 : num
    if (numAmount > 0 && numAmount < minAmount) {
      return `< ${minAmount}`
    }

    const [integerPart, decimalPart] = amount.toString().split(".")

    /**
     * 1. we should handle large differently to avoid precision loss
     * 2. we should handle decimals differently to avoid precision loss (if too many decimals it will truncate)
     */
    if (numAmount > 1000000000000 || (decimalPart && decimalPart.length > 10)) {
      // split the decimals off and truncate on their own
      const truncatedDecimal = decimalPart?.slice(0, decimals)
      const integerFormatter = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      const formattedInteger = integerFormatter.format(Number(integerPart))

      // bugfix, remove decimals if user requested 0 decimals
      const parts = [formattedInteger]

      if (decimals > 0 && truncatedDecimal) {
        parts.push(truncatedDecimal)
      }
      return parts.join(".")
    }

    // format normally using JS methods which are NOT bigint or large decimals safe
    const formatter = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })

    const result = formatter.format(Number(amount))
    return result
  } else {
    return 0
  }
}

export const formatPercentage = (amount: string | number, decimals = 2) => {
  return formatCurrency(Number(amount) * 100, decimals) + "%"
}

export function formatNumber(num: number): string {
  if (num >= 1e12) {
    return (num / 1e12).toFixed(1).replace(/\.0$/, "") + "T"
  }
  if (num >= 1e9) {
    return (num / 1e9).toFixed(1).replace(/\.0$/, "") + "B"
  }
  if (num >= 1e6) {
    return (num / 1e6).toFixed(1).replace(/\.0$/, "") + "M"
  }
  if (num >= 1e3) {
    return (num / 1e3).toFixed(1).replace(/\.0$/, "") + "K"
  }
  return num.toString()
}

/**
 *
 * @param number
 * @param decimals
 * @param showDollar
 * @returns
 */
export function prettifyCurrency(number: number, decimals = 2, showDollar = true) {
  // Define the unit scales
  const units = ["K", "million", "billion", "trillion"]
  const spacer = ["", " ", " ", " "]

  if (Number.isNaN(number)) {
    return ""
  }

  // If the number is less than 1000, return it directly
  if (number < 1000) {
    if (!showDollar) {
      return `${number.toFixed(decimals)}`
    }
    return `$${number.toFixed(decimals)}`
  }

  // Calculate the appropriate scale
  const unitIndex = Math.floor(Math.log10(number) / 3)

  if (Number.isNaN(unitIndex)) {
    return ""
  }

  // If the unit index is larger than the length of our units array, we've exceeded our max supported value
  if (unitIndex > units.length) {
    throw new Error("Number is too large to format.")
  }

  // Calculate the scaled number
  const scaledNumber = number / Math.pow(1000, unitIndex)

  // Return the formatted number
  return `${showDollar ? "$" : ""}${scaledNumber.toFixed(decimals)}${spacer[unitIndex - 1]}${units[unitIndex - 1]}`
}

export const dollars = (number: number | string, decimals = 2, locale = "en-US") => {
  if (Number(number) < 0 || !number) {
    return `$${Number(0).toFixed(decimals)}`
  }
  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })

  return formatter.format(Number(number))
}

/**
 * Formats a USD amount as a user-facing string.
 *
 * Behaviour:
 *   - `0`, `NaN`, negatives → `"$0.00"` (preserves legacy semantics).
 *   - positive values smaller than what `decimals` can represent (e.g. 0.004
 *     with the default 2 decimals) → `"<$0.01"` — distinguishes "tiny
 *     non-zero" from "actually zero", which matters for position-level fees
 *     and balances that are naturally sub-cent.
 *   - otherwise delegates to {@link prettifyNumberUSD}.
 */
export const dollarsPrettified = (number: number | string, decimals = 2, locale = "en-US") => {
  const n = Number(number)
  if (!Number.isFinite(n) || n <= 0) return `$${(0).toFixed(decimals)}`
  const subCentThreshold = Math.pow(10, -decimals)
  if (n < subCentThreshold) {
    const zeros = decimals > 1 ? "0".repeat(decimals - 1) : ""
    return `<$0.${zeros}1`
  }
  return prettifyNumberUSD(n, decimals, locale)
}

export const prettifyNumberUSD = (number: number | string, decimals = 2, locale = "en-US") => {
  const units = ["", "K", "M", "B", "T"]
  const amount = Math.abs(Number(number)) || 0
  const tier = Math.floor(Math.log10(amount) / 3) || 0

  if (tier <= 0) {
    const formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
    return formatter.format(amount)
  }

  const suffix = units[tier]
  const scale = Math.pow(10, tier * 3)
  const scaled = amount / scale

  const parts = ["$", scaled.toFixed(decimals)]
  if (suffix) {
    parts.push(suffix)
  }

  return parts.join("")
}

export const prettifyNumber = (number: number | string, decimals = 2, locale = "en-US") => {
  const units = ["", "K", "M", "B", "T"]
  const amount = Math.abs(Number(number)) || 0
  const tier = Math.floor(Math.log10(amount) / 3) || 0

  if (tier <= 0) {
    const formatter = new Intl.NumberFormat(locale, {
      style: "decimal",
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
    return formatter.format(amount)
  }

  const suffix = units[tier]
  const scale = Math.pow(10, tier * 3)
  const scaled = amount / scale

  return `${scaled.toFixed(decimals)}${suffix}`
}

export function trimEthAddress(address: string, startChars = 4, endChars = 4, separator = "...") {
  if (!address) {
    return ""
  }

  const addressLength = address.length

  if (startChars + endChars >= addressLength) {
    return address
  }

  const start = address.slice(0, startChars)
  const end = address.slice(-endChars)

  if (startChars === 0) {
    return end
  }

  if (endChars === 0) {
    return start
  }

  return `${start}${separator}${end}`
}

export function toFormattedValueN(number: number) {
  return `${number.toLocaleString("en-GB", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  })}`
}
